import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { VivoService } from '../../vivo/vivo.service';
import { Linking } from '../linking.model';
import { LinkingCrudService } from '../linking.service';
import { BaseComponent } from '../../components/general/base/base.component';
import { Observable, Subject} from 'rxjs';
import { debounceTime, map, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { MapService } from '../../shared/map/map.service';
import { convertFeatureToMapObject } from '../../shared/utils';
import { MapObject } from '../../shared/map/types';
import { TranslateService } from '@ngx-translate/core';
import { ILayer } from '../../shared/map/layer/layer.type';

interface Pagination {
  limit: number;
  total: number;
  page: number;
}

@Component({
  selector: 'qm-linking-vivo',
  templateUrl: './linking-vivo.component.html',
  styleUrls: ['./linking-vivo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkingVivoComponent extends BaseComponent implements OnInit, OnDestroy {

  public assetList: any[];

  public editLink: string;
  public currentLink: MapObject = null;
  public mapObjectList: MapObject[] = [];
  public linkingListVivo: Linking[] = [];
  public selectedLayer: string;
  public layers: ILayer[];
  public regoId: string;
  public query: string;
  public autocompleteResults: MapObject[] = [];
  public pagination: Pagination = {
    limit: 300,
    total: 0,
    page: 1
  };
  public loading: boolean;
  private readonly ngUnsubscribe: Subject<boolean> = new Subject();

  public search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => {
        if (!term) {
          return [];
        }
        term = term.toLowerCase();
        return this.mapObjectList.filter(obj => obj.title && obj.title.toLowerCase().includes(term)).slice(0, 10);
      })
    )

  constructor(private readonly vivoService: VivoService,
    private readonly mapService: MapService,
    private readonly translateService: TranslateService,
    private readonly linkingCrudService: LinkingCrudService,
    private readonly changeDetectorRef: ChangeDetectorRef) {
    super();
    this.name = 'LinkingVivoComponent';
  }

  ngOnInit() {
    console.log('VIVO admin init');
    this.layers = this.mapService.getLayerList().filter(layer => layer.metadata.vivoLinkable);
    this.loadAssets();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private async loadAssets() {
    this.linkingListVivo = [];
    this.loading = true;
    const list = await this.linkingCrudService.filter({sourceSystem: 'vivo'});
    if (list.isOk) {
      for (const row of list.data) {
        this.linkingListVivo[row.sourceId] = row;
      }
    }

    const assets = await this.vivoService.getAllAssets(this.pagination.limit, this.pagination.page);
    if (assets) {
      this.pagination.limit = assets.limit;
      this.pagination.page = assets.page;
      this.pagination.total = assets.total;

      this.assetList = assets.docs;
      this.mapService.visibleMarkers$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(markers => {
        this.mapObjectList = Array.from(this.mapService.getLayerFeatures()).flatMap(([layerName, features]) => {
          return features.map(feature => convertFeatureToMapObject(feature));
        });
        this.mapObjectList = this.mapObjectList.concat(markers.map(marker => {
          const l = marker.layer.metadata;
          return {id: marker.properties[l.uniqueBy], title: marker.properties[l.labelBy], layerId: marker.layer.id} as MapObject;
        }));
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      });
      console.log(this.mapObjectList);
    }
  }

  public setEditLink(asset: string) {
    this.editLink = asset;
    this.currentLink = null;
  }

  public async saveLink() {
    if (this.regoId || this.currentLink) {
      const m = new Linking();
      if (this.regoId) {
        m.regoId = this.regoId;
      } else {
        m.regoId = this.currentLink.id;
      }
      m.sourceId = this.editLink;
      m.sourceSystem = 'vivo';
      m.sourceLinkType = this.getAssetType(this.editLink);
      this.linkingListVivo[this.editLink] = m;
      await this.linkingCrudService.create(m);
      this.currentLink = null;
      this.editLink = '';
      this.changeDetectorRef.markForCheck();
      this.loadAssets();
    }
  }

  public getMapObjectTitle(obj: MapObject): string {
    return obj.title;
  }

  public getTitle(id: string): string {
    const r = this.mapObjectList.find(o => {
      return `${o.id}` === `${id}`;
    });
    return r ? r.title : id;
  }

  public getAssetType(asset: any): any {
    return asset.organization ? 'Organization' : 'Asset';
  }

  public async unLink() {
    const link = this.linkingListVivo[this.editLink];
    await this.linkingCrudService.remove(link);
    this.editLink = '';
    this.loadAssets();
  }

  public turnPage(page: any) {
    if (page !== this.pagination.page) {
      this.pagination.page = page;
    }
    this.loadAssets();
  }

  public getAssetName(asset: any) {
    return asset.holder?.name ? `${asset.name}, ${asset.holder.name}` : asset.name;
  }
}
